import type { ClarificationHint } from './types';

/** How the two-phase hint reaches the row.
 *
 *  The backend answers a hint request from catalog facts first (≈4 s) and
 *  marks it `refining` while its Google-grounded pass (≈10 s more) runs in the
 *  background and overwrites the cache. Polling the SAME request is free once
 *  that lands (a cache hit), so the row upgrades in place without the user
 *  waiting 14 s for the first guidance. */
export const HINT_REFINE_INTERVAL_MS = 6000;
export const HINT_REFINE_MAX_POLLS = 4;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** Poll `request` until the hint stops being `refining`, then `apply` it.
 *  Gives up quietly after `maxPolls`, on a failed request, or once `isActive`
 *  says the caller is gone (unmounted, locked, superseded). */
export async function pollRefinedHint(
  request: () => Promise<ClarificationHint>,
  apply: (hint: ClarificationHint) => void,
  isActive: () => boolean = () => true,
  intervalMs: number = HINT_REFINE_INTERVAL_MS,
  maxPolls: number = HINT_REFINE_MAX_POLLS,
): Promise<void> {
  for (let attempt = 0; attempt < maxPolls; attempt++) {
    await wait(intervalMs);
    if (!isActive()) return;
    let next: ClarificationHint;
    try {
      next = await request();
    } catch {
      return;
    }
    if (!isActive() || !next?.generated) return;
    if (!next.refining) {
      apply(next);
      return;
    }
  }
}
